/**
 * StaffForm — Create/edit employee form
 *
 * Sections:
 * 1. Persoonlijk — naam, geboortedatum, adres, contact
 * 2. Dienstverband — functie, team, contract, datum in dienst
 * 3. Salaris — loongroep, uurloon, loontype, reiskosten
 * 4. Werkrooster — (alleen bij vast/min-max)
 * 5. Systeem — PIN, Salesforce koppeling
 */

import { useState } from 'react';
import type { Staff, WerkroosterDay } from '../../stores/workforce-store';

interface StaffFormProps {
  staff?: Staff | null;
  onSave: (data: Partial<Staff>) => Promise<void>;
  onCancel: () => void;
  onDelete?: (id: string) => Promise<void>;
}

export function StaffForm({ staff, onSave, onCancel, onDelete }: StaffFormProps) {
  const [form, setForm] = useState({
    // Persoonlijk
    name: staff?.name || '',
    aanhef: staff?.aanhef || '',
    voorletters: staff?.voorletters || '',
    geboortedatum: staff?.geboortedatum || '',
    geboorteplaats: staff?.geboorteplaats || '',
    nationaliteit: staff?.nationaliteit || 'NL',
    bsn: staff?.bsn || '',
    // Adres
    adres: staff?.adres || '',
    postcode: staff?.postcode || '',
    stad: staff?.stad || '',
    // Contact
    email: staff?.email || '',
    phone: staff?.phone || '',
    noodnummer: staff?.noodnummer || '',
    noodcontact_naam: staff?.noodcontact_naam || '',
    // Financieel persoonlijk
    bankrekening: staff?.bankrekening || '',
    // Dienstverband
    functie: staff?.functie || '',
    team: staff?.team || 'algemeen',
    contract_type: staff?.contract_type || '0-uren',
    start_date: staff?.start_date || '',
    end_date: staff?.end_date || '',
    uren_per_week: staff?.uren_per_week?.toString() || '',
    fte_percentage: staff?.fte_percentage?.toString() || '100',
    // Salaris
    loongroep: staff?.loongroep?.toString() || '1',
    uurloon: staff?.uurloon?.toString() || '',
    loon_type: staff?.loon_type || 'bruto',
    uurloon_handmatig: staff?.uurloon_handmatig ? 'true' : 'false',
    cao_ervaringsjaren: staff?.cao_ervaringsjaren?.toString() ?? '',
    reiskostenvergoeding_per_dag: staff?.reiskostenvergoeding_per_dag?.toString() || '0',
    // Systeem
    pin_code: staff?.pin_code || '',
  });
  const [isSaving, setIsSaving] = useState(false);

  const DAY_LABELS = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'] as const;
  const WORK_DAYS = [1, 2, 3, 4, 5, 6] as const;

  const initRooster = (): Record<number, { active: boolean; from: string; to: string }> => {
    const result: Record<number, { active: boolean; from: string; to: string }> = {};
    for (const d of WORK_DAYS) {
      const existing = staff?.werkrooster?.find(w => w.day === d);
      result[d] = existing
        ? { active: true, from: existing.from, to: existing.to }
        : { active: false, from: '07:00', to: '16:00' };
    }
    return result;
  };
  const [werkrooster, setWerkrooster] = useState(initRooster);

  const showWerkrooster = form.contract_type === 'vast' || form.contract_type === 'min-max' || form.contract_type === 'jaaruren';
  const isEditing = !!staff;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave({
        name: form.name,
        aanhef: form.aanhef || null,
        voorletters: form.voorletters || null,
        geboortedatum: form.geboortedatum || null,
        geboorteplaats: form.geboorteplaats || null,
        nationaliteit: form.nationaliteit || null,
        bsn: form.bsn || null,
        adres: form.adres || null,
        postcode: form.postcode || null,
        stad: form.stad || null,
        woonplaats: form.stad || null, // keep woonplaats in sync with stad
        email: form.email || null,
        phone: form.phone || null,
        noodnummer: form.noodnummer || null,
        noodcontact_naam: form.noodcontact_naam || null,
        bankrekening: form.bankrekening || null,
        functie: form.functie || null,
        team: (form.team || null) as Staff['team'],
        contract_type: form.contract_type as Staff['contract_type'],
        start_date: form.start_date || null,
        datum_in_dienst: form.start_date || null, // keep in sync
        end_date: form.end_date || null,
        uren_per_week: form.uren_per_week ? parseFloat(form.uren_per_week) : null,
        fte_percentage: form.fte_percentage ? parseFloat(form.fte_percentage) : null,
        loongroep: form.loongroep ? parseInt(form.loongroep, 10) : null,
        uurloon: form.uurloon ? parseFloat(form.uurloon) : null,
        loon_type: form.loon_type as 'bruto' | 'all-in',
        uurloon_handmatig: form.uurloon_handmatig === 'true',
        cao_ervaringsjaren: form.cao_ervaringsjaren.trim() !== '' && !Number.isNaN(parseInt(form.cao_ervaringsjaren, 10))
          ? parseInt(form.cao_ervaringsjaren, 10)
          : null,
        reiskostenvergoeding_per_dag: form.reiskostenvergoeding_per_dag ? parseFloat(form.reiskostenvergoeding_per_dag) : 0,
        pin_code: form.pin_code || null,
        werkrooster: showWerkrooster
          ? Object.entries(werkrooster)
              .filter(([, v]) => v.active)
              .map(([d, v]) => ({ day: Number(d) as WerkroosterDay['day'], from: v.from, to: v.to }))
          : null,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [field]: e.target.value }));

  const inputClass = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none';
  const labelClass = 'block text-xs font-medium text-slate-500 mb-1';
  const sectionClass = 'text-xs font-semibold text-slate-400 uppercase tracking-wider';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-lg font-semibold text-slate-900">
        {isEditing ? 'Medewerker bewerken' : 'Nieuwe medewerker'}
      </h2>

      {/* ── 1. PERSOONLIJK ── */}
      <section className="space-y-4">
        <h3 className={sectionClass}>Persoonlijk</h3>
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-6 sm:col-span-3">
            <label className={labelClass}>Naam *</label>
            <input type="text" required value={form.name} onChange={set('name')} className={inputClass} />
          </div>
          <div className="col-span-3 sm:col-span-1">
            <label className={labelClass}>Aanhef</label>
            <select value={form.aanhef} onChange={set('aanhef')} className={inputClass}>
              <option value="">—</option>
              <option value="De heer">Dhr</option>
              <option value="Mevrouw">Mevr</option>
            </select>
          </div>
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Voorletters</label>
            <input type="text" value={form.voorletters} onChange={set('voorletters')} className={inputClass} placeholder="J.A." />
          </div>

          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Geboortedatum</label>
            <input type="date" value={form.geboortedatum} onChange={set('geboortedatum')} className={inputClass} />
          </div>
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Geboorteplaats</label>
            <input type="text" value={form.geboorteplaats} onChange={set('geboorteplaats')} className={inputClass} />
          </div>
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Nationaliteit</label>
            <input type="text" value={form.nationaliteit} onChange={set('nationaliteit')} className={inputClass} />
          </div>

          <div className="col-span-6 sm:col-span-2">
            <label className={labelClass}>BSN</label>
            <input type="text" value={form.bsn} onChange={set('bsn')} className={inputClass} />
          </div>
        </div>
      </section>

      <hr className="border-gray-100" />

      {/* ── ADRES & CONTACT ── */}
      <section className="space-y-4">
        <h3 className={sectionClass}>Adres & Contact</h3>
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-6 sm:col-span-3">
            <label className={labelClass}>Adres</label>
            <input type="text" value={form.adres} onChange={set('adres')} className={inputClass} placeholder="Straat + huisnummer" />
          </div>
          <div className="col-span-3 sm:col-span-1">
            <label className={labelClass}>Postcode</label>
            <input type="text" value={form.postcode} onChange={set('postcode')} className={inputClass} placeholder="1234AB" />
          </div>
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Plaats</label>
            <input type="text" value={form.stad} onChange={set('stad')} className={inputClass} />
          </div>

          <div className="col-span-6 sm:col-span-2">
            <label className={labelClass}>Email</label>
            <input type="email" value={form.email} onChange={set('email')} className={inputClass} />
          </div>
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Telefoon</label>
            <input type="tel" value={form.phone} onChange={set('phone')} className={inputClass} />
          </div>
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Noodnummer</label>
            <input type="tel" value={form.noodnummer} onChange={set('noodnummer')} className={inputClass} />
          </div>
          <div className="col-span-6 sm:col-span-3">
            <label className={labelClass}>Noodcontact (naam)</label>
            <input type="text" value={form.noodcontact_naam} onChange={set('noodcontact_naam')} className={inputClass} placeholder="Wie waarschuwen bij noodgeval" />
          </div>

          <div className="col-span-6 sm:col-span-3">
            <label className={labelClass}>Bankrekening (IBAN)</label>
            <input type="text" value={form.bankrekening} onChange={set('bankrekening')} className={inputClass} placeholder="NL00BANK0000000000" />
          </div>
        </div>
      </section>

      <hr className="border-gray-100" />

      {/* ── 2. DIENSTVERBAND ── */}
      <section className="space-y-4">
        <h3 className={sectionClass}>Dienstverband</h3>
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-6 sm:col-span-3">
            <label className={labelClass}>Functie</label>
            <input type="text" value={form.functie} onChange={set('functie')} className={inputClass} placeholder="All-Round Medewerker" />
          </div>
          <div className="col-span-3 sm:col-span-3">
            <label className={labelClass}>Team</label>
            <select value={form.team} onChange={set('team')} className={inputClass}>
              <option value="wasserij">Wasserij</option>
              <option value="route">Route</option>
              <option value="kantoor">Kantoor</option>
              <option value="algemeen">Algemeen</option>
            </select>
            <p className="mt-1 text-[11px] text-slate-400">
              Het team bepaalt de pauzeregels voor klok en urensaldo (als er geen shift in het rooster staat).
            </p>
            {form.team === 'algemeen' && (form.contract_type === 'vast' || form.contract_type === 'min-max' || form.contract_type === 'jaaruren') && (
              <p className="mt-1 text-[11px] text-amber-600 font-medium">
                Let op: bij team "Algemeen" vallen de pauzeregels terug op het wettelijk minimum
                (30 min boven 5,5u, anders geen pauze). Kies wasserij, route of kantoor voor het juiste urensaldo.
              </p>
            )}
          </div>

          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Contract type *</label>
            <select required value={form.contract_type} onChange={set('contract_type')} className={inputClass}>
              <option value="vast">Vast</option>
              <option value="jaaruren">Jaaruren</option>
              <option value="vast-salaris">Vast salaris</option>
              <option value="min-max">Min-max</option>
              <option value="0-uren">0-uren</option>
              <option value="student">Student</option>
              <option value="zzp">ZZP</option>
              <option value="dga">DGA</option>
            </select>
          </div>
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Datum in dienst</label>
            <input type="date" value={form.start_date} onChange={set('start_date')} className={inputClass} />
          </div>
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Uit dienst per</label>
            <input type="date" value={form.end_date} onChange={set('end_date')} className={inputClass} />
            <p className="mt-1 text-[10px] text-slate-400">Alleen invullen als de medewerker vertrekt; contracteindes staan op het contract.</p>
          </div>

          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Uren per week</label>
            <input type="number" step="0.5" value={form.uren_per_week} onChange={set('uren_per_week')} className={inputClass} placeholder="40" />
          </div>
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>FTE %</label>
            <input type="number" step="1" min="0" max="100" value={form.fte_percentage} onChange={set('fte_percentage')} className={inputClass} />
          </div>
        </div>
      </section>

      <hr className="border-gray-100" />

      {/* ── 3. SALARIS ── */}
      <section className="space-y-4">
        <h3 className={sectionClass}>Salaris</h3>
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Loongroep (CAO)</label>
            <select value={form.loongroep} onChange={set('loongroep')} className={inputClass}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(lg => (
                <option key={lg} value={lg}>Loongroep {lg}</option>
              ))}
            </select>
          </div>
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Uurloon</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-sm text-slate-400">&euro;</span>
              <input type="number" step="0.01" value={form.uurloon} onChange={set('uurloon')} className={`${inputClass} pl-7`} placeholder="14.50" />
            </div>
          </div>
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Loontype</label>
            <select value={form.loon_type} onChange={set('loon_type')} className={inputClass}>
              <option value="bruto">Bruto</option>
              <option value="all-in">All-in</option>
            </select>
          </div>

          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>CAO-ervaringsjaren (erkende ervaring elders)</label>
            <input
              type="number"
              step="1"
              min="0"
              value={form.cao_ervaringsjaren}
              onChange={set('cao_ervaringsjaren')}
              className={inputClass}
              placeholder="0"
            />
            <p className="mt-1 text-[11px] text-slate-400">
              Telt op bij de dienstjaren voor de CAO-trede en groeit mee (bv. 4 = trede 4 bij instroom).
            </p>
          </div>

          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>Reiskostenvergoeding/dag</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-sm text-slate-400">&euro;</span>
              <input type="number" step="0.01" min="0" value={form.reiskostenvergoeding_per_dag} onChange={set('reiskostenvergoeding_per_dag')} className={`${inputClass} pl-7`} />
            </div>
          </div>
          <div className="col-span-3 sm:col-span-4">
            <label className={labelClass}>&nbsp;</label>
            <label className="flex items-center gap-2 px-3 py-2">
              <input
                type="checkbox"
                checked={form.uurloon_handmatig === 'true'}
                onChange={e => setForm(f => ({ ...f, uurloon_handmatig: e.target.checked ? 'true' : 'false' }))}
                className="rounded border-gray-300"
              />
              <span className="text-xs text-slate-500">Uurloon handmatig beheren (niet bijwerken via CAO sync)</span>
            </label>
          </div>
        </div>
      </section>

      {/* ── 4. WERKROOSTER ── */}
      {showWerkrooster && (
        <>
          <hr className="border-gray-100" />
          <section className="space-y-3">
            <h3 className={sectionClass}>Werkrooster</h3>
            <div className="space-y-1.5">
              {WORK_DAYS.map(d => {
                const day = werkrooster[d]!;
                const updateDay = (patch: Partial<{ active: boolean; from: string; to: string }>) => {
                  setWerkrooster(prev => ({
                    ...prev,
                    [d]: { ...prev[d]!, ...patch },
                  }));
                };
                return (
                  <div key={d} className="flex items-center gap-3">
                    <label className="flex items-center gap-2 w-16 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={day.active}
                        onChange={e => updateDay({ active: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm font-medium text-slate-700 capitalize">{DAY_LABELS[d]}</span>
                    </label>
                    {day.active ? (
                      <div className="flex items-center gap-2">
                        <input type="time" value={day.from} onChange={e => updateDay({ from: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none" />
                        <span className="text-slate-400 text-sm">—</span>
                        <input type="time" value={day.to} onChange={e => updateDay({ to: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none" />
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">Vrij</span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}

      <hr className="border-gray-100" />

      {/* ── 5. SYSTEEM ── */}
      <section className="space-y-4">
        <h3 className={sectionClass}>Systeem</h3>
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-3 sm:col-span-2">
            <label className={labelClass}>PIN (4 cijfers)</label>
            <input
              type="text"
              maxLength={4}
              pattern="[0-9]{4}"
              value={form.pin_code}
              onChange={e => setForm(f => ({ ...f, pin_code: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
              className={inputClass}
              placeholder="1234"
            />
          </div>

          {staff?.sf_medewerker_id && (
            <div className="col-span-6 sm:col-span-4">
              <label className={labelClass}>Salesforce</label>
              <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                Gekoppeld ({staff.sf_medewerker_id})
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── ACTIONS ── */}
      <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
        <button
          type="submit"
          disabled={isSaving || !form.name}
          className="px-6 py-2.5 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 disabled:opacity-50 transition-colors"
        >
          {isSaving ? 'Opslaan...' : isEditing ? 'Opslaan' : 'Toevoegen'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2.5 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium transition-colors"
        >
          Annuleren
        </button>
        {isEditing && onDelete && (
          <button
            type="button"
            onClick={() => onDelete(staff!.id)}
            className="ml-auto px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
          >
            Deactiveren
          </button>
        )}
      </div>
    </form>
  );
}
